import { useRef, useState } from "react";
import { UploadCloud, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { invoiceGenUpload } from "@/lib/api";

export default function UploadStep({ onUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted.");
      return;
    }
    setFileName(file.name);
    setBusy(true);
    try {
      const res = await invoiceGenUpload(file);
      (res.warnings || []).forEach((w) => toast.warning(w));
      if (!res.invoices || res.invoices.length === 0) {
        toast.error("No invoice transactions were detected in this statement.");
      }
      onUploaded(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read this statement PDF.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-[#6B7280] mb-6">
        Upload a Statement PDF from the billing system. It may contain one invoice, many invoices,
        historical transactions and payments across multiple pages — you'll pick the exact invoice
        to convert on the next step.
      </p>
      <div
        data-testid="invoice-gen-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#3A7A8C] bg-[#F0F7F8]" : "border-[#D1D5DB] bg-white"
        }`}
      >
        {busy ? (
          <>
            <Loader2 className="animate-spin text-[#3A7A8C]" size={32} />
            <p className="mt-4 text-sm text-[#3C4048]">Reading {fileName}…</p>
          </>
        ) : fileName ? (
          <>
            <FileText className="text-[#3A7A8C]" size={32} />
            <p className="mt-4 text-sm text-[#3C4048]">{fileName}</p>
          </>
        ) : (
          <>
            <UploadCloud className="text-[#9CA3AF]" size={32} />
            <p className="mt-4 text-sm font-semibold text-[#080808]">
              Drag & drop the Statement PDF here
            </p>
            <p className="mt-1 text-xs text-[#6B7280]">or click to choose a file — PDF only</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          data-testid="invoice-gen-file-input"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}
